import { CopyButton } from "@components/shared/CopyButton";
import { GENERATE_ASSESSMENT_GUIDE, UPDATE_ASSESSMENT_GUIDE } from "@lib/graphql/mutations";
import { SanitizedHtml } from "@lib/SanitizedHtml";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { ChevronDown, ChevronRight, ClipboardCheck, Loader2, Pencil, Sparkles } from "lucide-react";
import { useRef, useState } from "react";
import { useMutation } from "urql";

interface AssessmentGuideAuthor {
  id: string;
  name: string;
}

interface AssessmentGuideVersionData {
  id: string;
  versionNumber: number;
  changeSummary: string | null;
  effectiveFrom: string;
  author: AssessmentGuideAuthor;
}

interface AssessmentGuideCurrentVersion extends AssessmentGuideVersionData {
  content: string | null;
  generationState?: string;
  generationError?: string | null;
  model?: string | null;
  assessmentMatrixVersionNumber?: number | null;
}

export interface AssessmentGuideData {
  id: string;
  totalVersions: number;
  currentVersion: AssessmentGuideCurrentVersion | null;
  versions: AssessmentGuideVersionData[];
}

interface AssessmentGuidePanelProps {
  campaignId: string;
  guide: AssessmentGuideData | null;
  // Whether the matrix exists — Generate is gated on a matrix being present so
  // the LLM has bands to derive evidence indicators from.
  matrixReady?: boolean;
  onSaved?: () => void;
  defaultCollapsed?: boolean;
}

export function AssessmentGuidePanel({
  campaignId,
  guide,
  matrixReady = false,
  onSaved,
  defaultCollapsed = false,
}: AssessmentGuidePanelProps) {
  const [editing, setEditing] = useState(false);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [{ fetching: saving }, updateGuide] = useMutation(UPDATE_ASSESSMENT_GUIDE);
  const [{ fetching: generating }, generate] = useMutation(GENERATE_ASSESSMENT_GUIDE);
  const bodyRef = useRef<HTMLDivElement>(null);

  const currentVersion = guide?.currentVersion ?? null;
  const hasGuide = guide !== null && currentVersion !== null;
  const aiGenerating = currentVersion?.generationState === "generating";

  const generateDisabled = !matrixReady || aiGenerating || generating;
  const generateTitle = !matrixReady
    ? "Generate the assessment matrix first — the guide derives evidence indicators from its bands"
    : aiGenerating
      ? "Generation already in progress"
      : hasGuide
        ? "Regenerate the Assessment Guide from the current matrix"
        : "Generate the Assessment Guide from the current matrix";

  const handleGenerate = async () => {
    if (generateDisabled) return;
    await generate({ input: { campaignId, force: hasGuide, confirmOverwrite: false } });
    onSaved?.();
  };

  const editor = useEditor({
    extensions: [StarterKit],
    content: currentVersion?.content ?? "",
    immediatelyRender: false,
  });

  return (
    <div
      className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
      data-testid="assessment-guide-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed || editing}
          aria-label={collapsed ? "Show Assessment Guide" : "Hide Assessment Guide"}
          className="flex items-center gap-2 rounded text-left hover:bg-card-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {collapsed && !editing ? (
            <ChevronRight size={16} className="text-text-faint" />
          ) : (
            <ChevronDown size={16} className="text-text-faint" />
          )}
          <ClipboardCheck size={18} className="text-text-faint" />
          <div>
            <h2 className="text-sm font-medium text-text-primary">Assessment Guide</h2>
            <p className="text-xs text-text-faint">
              {hasGuide
                ? `Version ${currentVersion.versionNumber} · ${guide.totalVersions} total`
                : "No Assessment Guide yet"}
            </p>
          </div>
        </button>
        <div className="flex items-center gap-2">
          {hasGuide && (
            <CopyButton
              ariaLabel="Copy Assessment Guide"
              withLabel
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover"
              getContent={() => {
                const el = bodyRef.current;
                if (!el) return { html: "", text: "" };
                return {
                  html: el.innerHTML,
                  text: el.innerText || el.textContent || "",
                };
              }}
            />
          )}
          {!editing && (
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generateDisabled}
              title={generateTitle}
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {aiGenerating ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Sparkles size={14} />
              )}
              {hasGuide ? "Regenerate" : "Generate from matrix"}
            </button>
          )}
          {!editing && hasGuide && (
            <button
              type="button"
              onClick={() => {
                setCollapsed(false);
                setEditing(true);
              }}
              className="flex items-center gap-1 rounded bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent/90"
            >
              <Pencil size={14} />
              Edit
            </button>
          )}
        </div>
      </div>

      {!editing && hasGuide && !collapsed && (
        <div className="mt-3">
          <SanitizedHtml
            ref={bodyRef}
            className="prose prose-sm dark:prose-invert max-w-none text-text-primary"
            html={currentVersion.content}
          />
        </div>
      )}

      {!editing && !hasGuide && !collapsed && (
        <p className="mt-3 text-sm text-text-muted">
          The Assessment Guide owns the canonical evidence indicators (1-2 / 3 / 4-5 bands) for
          every capability dimension. Generate it from the assessment matrix to align everyone on
          how to score.
        </p>
      )}

      {editing && editor && (
        <div className="mt-3 space-y-2">
          <EditorContent
            editor={editor}
            className="min-h-[200px] rounded border border-border bg-background p-3"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded px-2 py-1 text-xs text-text-secondary hover:bg-card-hover"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={async () => {
                const content = editor?.getHTML() ?? "";
                const result = await updateGuide({
                  input: { campaignId, content, changeSummary: null },
                });
                if (result.data?.updateAssessmentGuide?.errors?.length === 0) {
                  setEditing(false);
                  onSaved?.();
                }
              }}
              className="flex items-center gap-1 rounded bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
